// The entry screens' anatomy (APS-323; styles/entry.css): a bare page on the dot-grid plate with
// the wordmark, one 440 px column with a head (mono kicker, display title, lede), notes, and the
// form as the one window. Sign-in, the first run's two steps, an invitation, 404 and the return
// from Stripe use it. No link to the sandbox (D105).
import type { ReactNode } from 'react'
import { Mark } from '../Mark'
import { Tag } from './Controls'

/** `tag` marks which console it is beside the wordmark (the admin console's ADMIN). */
export function EntryPage({ children, tag }: { children: ReactNode; tag?: ReactNode }) {
  return (
    <div className="en-pg">
      <header className="en-top">
        <span className="en-brand">
          <Mark />
          <span translate="no">apsio</span>
        </span>
        {tag ? <Tag>{tag}</Tag> : null}
      </header>
      <main id="main" className="en-col">
        {children}
      </main>
    </div>
  )
}

export function EntryHead({
  kicker,
  title,
  lede,
}: {
  kicker?: ReactNode
  title: ReactNode
  lede?: ReactNode
}) {
  return (
    <div className="en-hd">
      {kicker ? <p className="en-kick">{kicker}</p> : null}
      <h1 className="en-h">{title}</h1>
      {lede ? <p className="en-lede">{lede}</p> : null}
    </div>
  )
}

const GLYPH = { warn: '!', crash: '!', ok: '✓', mute: '@' } as const

/**
 * A note above the window: why something was refused, or what to know first. A `crash` note is
 * an alert; the others are a status. The glyph is decoration.
 */
export function EntryNote({
  tone = 'warn',
  glyph,
  title,
  children,
  after,
}: {
  tone?: keyof typeof GLYPH
  glyph?: string
  title?: ReactNode
  children?: ReactNode
  /** What belongs to the note, under its text after a hairline (a list of rows). */
  after?: ReactNode
}) {
  return (
    <div className="en-msg" role={tone === 'crash' ? 'alert' : 'status'}>
      <span className={tone === 'warn' ? 'en-g' : `en-g ${tone}`} aria-hidden="true">
        {glyph ?? GLYPH[tone]}
      </span>
      <div>
        {title ? <b>{title}</b> : null}
        {children ? <p>{children}</p> : null}
        {after ? <div className="en-msg-after">{after}</div> : null}
      </div>
    </div>
  )
}

/** Something under way (accepting an invitation, checking a card): a pulsing point and words. */
export function EntryWait({ children }: { children: ReactNode }) {
  return (
    <p className="en-wait" role="status">
      <i aria-hidden="true" />
      {children}
    </p>
  )
}
